<template>
  <layout title="YouAuth" :transparent="true" class="overflow-x-hidden">
    <highlightSection
      backgroundImage="/storage/images/products/youAuth/biometric_face_authentication.jpg"
      altImage="Biometric Face Authentication"
      title="Decentralized biometric authentication"
      subtitle="Deliver secure and decentralized biometric authentication experiences to your customers."
      leftButton="See how it works"
      urlLeftButton="https://6992654.fs1.hubspotusercontent-na1.net/hubfs/6992654/01.%20ONE%20PAGERS/Youverse_YouAuth.pdf"
      :imageLeft="true"
    />
    <section1
      title="YouAuth: Passwordless authentication that preserves privacy"
      imageSrc="/storage/images/products/youAuth/biometric_user_authentication.png"
      imageAlt="Biometric User Authentication"
    />
    <section2
      title="Seamless and decentralized user authentication"
      :elements="[
        {
          title: `Seamless and scalable authentication`,
          text: `YouAuth enables passwordless login, eliminating the frustration of forgotten passwords and reducing operational costs related to password resets. It's compatible with standard identity providers for easy integration with existing tech stacks.`,
        },
        {
          title: `Zero-trust authentication and liveness detection`,
          text: `YouAuth employs advanced liveness detection and fraud detection techniques to counter deepfake attacks and other sophisticated fraud attempts. With a decentralized, privacy-first data architecture, user data is never centralized or vulnerable to large-scale breaches.`,
        },
        {
          title: `Unparalleled performance and security`,
          text: `Top-tier NIST algorithms for unmatched security and reliability. 7x faster than standard encryption templates, delivering rapid authentication without compromising safety.`,
        },
      ]"
    />

    <div class="my-10">
      <cta
        title="Get started"
        text="See YouAuth in action and explore how it can fit your business needs."
        buttonText="Book a Demo"
        buttonLink="/book-a-meeting"
        target="_blank"
      />
    </div>

    <section3
      title="See how YouAuth works in your industry"
      :elements="[
        {
          title: `Banking`,
          text: `Accelerate onboarding, minimize fraud, and automate KYC/AML checks.`,
          seeMoreUrl: `/use-cases/banking`,
          imageSrc: `/storage/images/home/icons/banking.svg`,
          imageAlt: `Banking`,
          imageStyle: `w-20 h-20`,
        },
        {
          title: `iGaming`,
          text: `Simplify player verification, boost onboarding and ensure regulatory compliance.`,
          seeMoreUrl: `/use-cases/igaming`,
          imageSrc: `/storage/images/home/icons/igaming.svg`,
          imageAlt: `iGaming`,
          imageStyle: `w-20 h-20`,
        },
        {
          title: `Hospitality`,
          text: `Ensure operational efficiency, protect guests’ data, and eliminate check-in friction.`,
          seeMoreUrl: `/use-cases/hospitality`,
          imageSrc: `/storage/images/home/icons/hospitality.svg`,
          imageAlt: `Hospitality`,
          imageStyle: `w-20 h-20`,
        },
        {
          title: `Mobility`,
          text: `Onboard customers instantly, ensure robust driver’s verification, and gain your customers trust.`,
          seeMoreUrl: `/use-cases/mobility`,
          imageSrc: `/storage/images/home/icons/mobility.svg`,
          imageAlt: `Mobility`,
          imageStyle: `w-20 h-20`,
        },
      ]"
    />

    <textImageSection
      title="Choose your plan"
      text="Choose your plan and unlock the full potential of top-tier face matching."
      imageSrc="/storage/images/products/face/youverse_authentication_pricing.png"
      imageAlt="Biometric Authentication Id Verification"
      buttonText="Pick your plan"
      buttonLink="/pricing"
    />

    <providersSection />

    <latestPosts
      title="Read our latest articles and resources"
      :tags="['authentication']"
    />
    <div class="mt-28">
      <cta
        title="Start your free trial today"
        text="Explore YouAuth’s features at no cost and experience the difference firsthand."
        buttonText="Contact Us"
        buttonLink="/company#contactus"
      />
    </div>
  </layout>
</template>

<script>
const layout = () =>
  import(/* webpackChunkName: "layout" */ "../../Components/Layout/layout.vue");

const highlightSection = () =>
  import(
    /* webpackChunkName: "highlightSection" */ "../../Components/Layout/highlightSection.vue"
  );

const section1 = () =>
  import(
    /* webpackChunkName: "section1" */ "../../Components/Layout/Products/section1.vue"
  );

const section2 = () =>
  import(
    /* webpackChunkName: "section2" */ "../../Components/Layout/Products/section2.vue"
  );

const cta = () =>
  import(/* webpackChunkName: "cta" */ "../../Components/Layout/cta.vue");

const section3 = () =>
  import(
    /* webpackChunkName: "section3" */ "../../Components/Layout/Products/section3.vue"
  );

const textImageSection = () =>
  import(
    /* webpackChunkName: "textImageSection" */ "../../Components/Layout/textImageSection.vue"
  );

const providersSection = () =>
  import(
    /* webpackChunkName: "providersSection" */ "../../Components/Layout/Products/providersSection.vue"
  );

const latestPosts = () =>
  import(
    /* webpackChunkName: "latestPosts" */ "../../Components/Layout/latestPosts.vue"
  );

export default {
  components: {
    layout,
    highlightSection,
    section1,
    section2,
    cta,
    section3,
    textImageSection,
    providersSection,
    latestPosts,
  },
  props: {
    errors: Object,
  },
};
</script>

